import { supabase } from '../lib/supabaseClient';
import { logger } from '../utils/logger';
import { NotificationService } from './notificationService';

export interface LicenseData {
  type?: 'PPL' | 'CPL' | 'ATP' | 'SPL' | 'ULM' | 'other';
  number?: string;
  country?: string;
  expiryDate?: string;
  ratings?: string[];
  totalHours?: number;
  notes?: string;
}

export interface MembershipRequest {
  id: string;
  orgId: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  licenseData?: LicenseData;
  status: 'pending' | 'approved' | 'rejected';
  userId?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  rejectionReason?: string;
  requestedAt: string;
  updatedAt: string;
}

export interface CreateRequestData {
  orgId: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  licenseData?: LicenseData;
  userId?: string; // If user already exists
}

export class MembershipRequestService {
  /**
   * Create a new membership request
   */
  static async createRequest(data: CreateRequestData): Promise<MembershipRequest> {
    const requestId = crypto.randomUUID();
    const now = new Date().toISOString();

    // Check if user already exists
    let existingUserId: string | undefined;
    if (data.userId) {
      existingUserId = data.userId;
    } else {
      const { data: existingUser } = await supabase
        .from('users')
        .select('id, email, first_name, last_name')
        .eq('email', data.email.toLowerCase().trim())
        .maybeSingle();

      if (existingUser) {
        existingUserId = existingUser.id;
        logger.info(`[MembershipRequestService] Found existing user for email ${data.email}, linking request to account`);
      }
    }

    const requestData = {
      id: requestId,
      organization_id: data.orgId,
      email: data.email.toLowerCase().trim(),
      first_name: data.firstName,
      last_name: data.lastName,
      phone_number: data.phoneNumber,
      license_data: data.licenseData || null,
      status: 'pending',
      user_id: existingUserId || null,
      requested_at: now,
      updated_at: now,
    };

    const { data: inserted, error } = await supabase
      .from('membership_requests')
      .insert(requestData)
      .select()
      .single();

    if (error) {
      logger.error('[MembershipRequestService] Error creating request:', error);
      throw error;
    }

    return this.mapRowToRequest(inserted);
  }

  /**
   * Get pending requests for an organization
   */
  static async getPendingRequests(orgId: string): Promise<MembershipRequest[]> {
    const { data, error } = await supabase
      .from('membership_requests')
      .select('*')
      .eq('organization_id', orgId)
      .eq('status', 'pending')
      .order('requested_at', { ascending: false });

    if (error) {
      logger.error('[MembershipRequestService] Error fetching pending requests:', error);
      throw error;
    }

    return (data || []).map(row => this.mapRowToRequest(row));
  }

  /**
   * Get all requests for an organization (all statuses)
   */
  static async getAllRequests(orgId: string): Promise<MembershipRequest[]> {
    const { data, error } = await supabase
      .from('membership_requests')
      .select('*')
      .eq('organization_id', orgId)
      .order('requested_at', { ascending: false });

    if (error) {
      logger.error('[MembershipRequestService] Error fetching requests:', error);
      throw error;
    }

    return (data || []).map(row => this.mapRowToRequest(row));
  }

  /**
   * Get request by ID
   */
  static async getRequest(requestId: string): Promise<MembershipRequest | null> {
    const { data, error } = await supabase
      .from('membership_requests')
      .select('*')
      .eq('id', requestId)
      .maybeSingle();

    if (error) {
      logger.error('[MembershipRequestService] Error fetching request:', error);
      throw error;
    }

    if (!data) {
      return null;
    }

    return this.mapRowToRequest(data);
  }

  /**
   * Get request by email and organization
   */
  static async getRequestByEmail(
    email: string,
    orgId: string
  ): Promise<MembershipRequest | null> {
    const { data, error } = await supabase
      .from('membership_requests')
      .select('*')
      .eq('email', email.toLowerCase().trim())
      .eq('organization_id', orgId)
      .eq('status', 'pending')
      .maybeSingle();

    if (error) {
      logger.error('[MembershipRequestService] Error fetching request by email:', error);
      throw error;
    }

    if (!data) {
      return null;
    }

    return this.mapRowToRequest(data);
  }

  /**
   * Check if user has pending request for organization
   */
  static async hasPendingRequest(
    email: string,
    orgId: string
  ): Promise<boolean> {
    const request = await this.getRequestByEmail(email, orgId);
    return request !== null;
  }

  /**
   * Approve request (creates user/membership if needed)
   */
  static async approveRequest(
    requestId: string,
    reviewedBy: string
  ): Promise<void> {
    const request = await this.getRequest(requestId);
    if (!request) {
      throw new Error('Request not found');
    }

    if (request.status !== 'pending') {
      throw new Error(`Cannot approve request with status: ${request.status}`);
    }

    const now = new Date().toISOString();

    // Check if user already exists
    let userId = request.userId;
    
    if (!userId) {
      // Check if user exists by email
      const { data: existingUser } = await supabase
        .from('users')
        .select('id')
        .eq('email', request.email)
        .maybeSingle();

      if (existingUser) {
        userId = existingUser.id;
      } else {
        // User doesn't exist yet - approval will create membership but user needs to create account
        // When user creates account with matching email, they'll be automatically linked
        logger.info('[MembershipRequestService] User does not exist yet, membership will be pending account creation');
      }
    }

    // If user exists, add them to organization
    if (userId) {
      // Add to organization_members
      const { error: memberError } = await supabase
        .from('organization_members')
        .upsert({
          org_id: request.orgId,
          user_id: userId,
          joined_at: now,
        }, {
          onConflict: 'org_id,user_id',
        });

      if (memberError) {
        logger.error('[MembershipRequestService] Error adding member:', memberError);
        throw memberError;
      }

      // Update user's org_id and role if not already set
      // Check current org_id first
      const { data: currentUser } = await supabase
        .from('users')
        .select('org_id, first_name, last_name, phone_number')
        .eq('id', userId)
        .single();

      // Only update if org_id is null (user not in another org)
      if (!currentUser?.org_id) {
        // Carry the name/phone the applicant already typed into the request
        // through to their real profile, so "Complete Your Profile" doesn't
        // ask the same three questions again. Only fills fields that are
        // still empty -- never overwrites an existing profile value.
        const profileFields: Record<string, string> = {};
        if (!currentUser?.first_name && request.firstName) {
          profileFields.first_name = request.firstName;
        }
        if (!currentUser?.last_name && request.lastName) {
          profileFields.last_name = request.lastName;
        }
        if (!currentUser?.phone_number && request.phoneNumber) {
          profileFields.phone_number = request.phoneNumber;
        }

        const { error: userError } = await supabase
          .from('users')
          .update({
            org_id: request.orgId,
            role: 'member' as const,
            updated_at: now,
            ...profileFields,
          })
          .eq('id', userId);

        if (userError) {
          logger.error('[MembershipRequestService] Error updating user:', userError);
          // Don't throw - membership was added successfully
        }

        // Best-effort: never block approval on email delivery.
        NotificationService.notifyMembershipRequestApproved(request.orgId, userId).catch(err =>
          logger.error('[MembershipRequestService] Failed to send approval email:', err)
        );
      } else {
        logger.info(`[MembershipRequestService] User already in organization ${currentUser.org_id}, keeping existing org`);
      }
    }

    // Update request status
    const { error: updateError } = await supabase
      .from('membership_requests')
      .update({
        status: 'approved',
        reviewed_by: reviewedBy,
        reviewed_at: now,
        updated_at: now,
      })
      .eq('id', requestId);

    if (updateError) {
      logger.error('[MembershipRequestService] Error updating request status:', updateError);
      throw updateError;
    }
  }

  /**
   * Reject request
   */
  static async rejectRequest(
    requestId: string,
    reviewedBy: string,
    reason: string
  ): Promise<void> {
    const request = await this.getRequest(requestId);
    if (!request) {
      throw new Error('Request not found');
    }

    if (request.status !== 'pending') {
      throw new Error(`Cannot reject request with status: ${request.status}`);
    }

    if (!reason || reason.trim().length === 0) {
      throw new Error('Rejection reason is required');
    }

    const now = new Date().toISOString();

    const { error } = await supabase
      .from('membership_requests')
      .update({
        status: 'rejected',
        reviewed_by: reviewedBy,
        reviewed_at: now,
        rejection_reason: reason.trim(),
        updated_at: now,
      })
      .eq('id', requestId);

    if (error) {
      logger.error('[MembershipRequestService] Error rejecting request:', error);
      throw error;
    }

    if (request.userId) {
      // Best-effort: never block rejection on email delivery.
      NotificationService.notifyMembershipRequestRejected(request.orgId, request.userId, reason.trim()).catch(err =>
        logger.error('[MembershipRequestService] Failed to send rejection email:', err)
      );
    }
  }

  /**
   * Map database row to MembershipRequest
   */
  private static mapRowToRequest(row: any): MembershipRequest {
    return {
      id: row.id,
      orgId: row.organization_id,
      email: row.email,
      firstName: row.first_name,
      lastName: row.last_name,
      phoneNumber: row.phone_number,
      licenseData: row.license_data || undefined,
      status: row.status,
      userId: row.user_id || undefined,
      reviewedBy: row.reviewed_by || undefined,
      reviewedAt: row.reviewed_at || undefined,
      rejectionReason: row.rejection_reason || undefined,
      requestedAt: row.requested_at,
      updatedAt: row.updated_at,
    };
  }
}

